import { useEffect } from "react"
import { Table } from "components/ui/tables/Table/Table"
import { useFillesTableStore, useTableStore } from "store/table.store"
import { FileInfo } from "../FileInfo/FileInfo"
import { useNatsProcessing } from "hooks/nats.hook"
import { useMessagesDataStore } from "store/nats.store"
import { DeleteConfirmationModal } from "./layout/DeleteConfirmationModal/DeleteConfirmationModal"
import { ChangeOwnerModal } from "./layout/ChangeOwnerModal/ChangeOwnerModal"
import { useDeleteConfirmModalStore } from "store/modal.store"
import { TableAllFilesHandlers } from "./TableAllFilesHandlers"
import { TableAllFilesColumns } from "./TableAllFilesColumns"
import { TableAllFilesUtils } from "./TableAllFilesUtils"
import { useDrawings } from "hooks/api/drawings/drawings.hook"
import { useNatsCreds, useUserProfile } from "hooks/api/user/user.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import type { TTable } from "types/table.type"
import style from "./TableAllFiles.module.scss"

const natsUrl = import.meta.env.VITE_NATS_URL

const { tableAllFIles } = style

export const TableAllFiles = () => {
	const { data, isLoading: isDrawingsLoading, isSuccess: isDrawingsSuccess, isError: isDrawingsError } = useDrawings()

	const { isSuccess: isUserProfileSuccess } = useUserProfile()
	const { data: natsCreds } = useNatsCreds()

	const records = data?.records

	const { onRowHandle, onChangeTableHandle, deleteFileHandle } = TableAllFilesHandlers()
	const columns = TableAllFilesColumns()
	const { initialTableData } = TableAllFilesUtils(records)

	const { messagesData } = useMessagesDataStore()
	const { tableParams, setCurrentPage } = useFillesTableStore()
	const { updatedTableData, setUpdatedTableData } = useTableStore()
	const { deleteFile } = useDeleteConfirmModalStore()

	// Connecting to NATS
	useNatsProcessing(natsUrl, "user.*.drawings.*.progress", "user.*.drawings.*.status", natsCreds)

	// Reset to first page if page does not exist
	useEffect(() => {
		if (data?.error === "The requested page does not exist") {
			setCurrentPage(1)
		}
	}, [data, setCurrentPage])

	// Update table data with new Status and Progress from Nats messages
	useEffect(() => {
		const updatedTableData = initialTableData.map((file) => {
			const fileId = file.id
			const fileName = file.name
			const owner = file.action.owner
			const processFilesIds = Object.keys(messagesData).map(Number)

			if (!processFilesIds.includes(fileId)) {
				return file // Return the original file if drawingId is not in drawingIds
			}

			const status = messagesData[fileId].status ? messagesData[fileId].status : file.status
			const progress = messagesData[fileId].progress

			// Return the updated file object
			return {
				...file,
				processing: {
					status: status && status.status,
					progress: progress,
					id: fileId,
					owner: owner,
				},
				status: {
					status: status && status.status,
				},
				action: {
					status: status && status.status,
					owner: owner,
					ownerId: file.action.ownerId,
					id: fileId,
					fileName: fileName,
				},
			}
		})

		// Update the state with the new table data
		setUpdatedTableData(updatedTableData as TTable[])
	}, [initialTableData, setUpdatedTableData, messagesData])

	return (
		<>
			{isDrawingsSuccess && isUserProfileSuccess ? (
				<Table
					className={`${tableAllFIles} TableAllFiles`}
					onChange={onChangeTableHandle}
					data={updatedTableData}
					onRow={onRowHandle}
					columns={columns}
					current={tableParams.pagination.current}
					total={data?.totalRecords}
				/>
			) : null}
			{isDrawingsLoading ? <Skeleton rows={15} /> : null}
			{isDrawingsError ? <div>Failed to load files. Please, reload the page or try again later</div> : null}

			<FileInfo />
			<DeleteConfirmationModal deleteFileHandle={deleteFileHandle} deleteFile={deleteFile} />
			<ChangeOwnerModal />
		</>
	)
}
